import React, { ReactNode, useCallback, useRef, useState } from 'react';

export interface TipRow {
    label: string;
    value: ReactNode;
    color?: string;
}

export interface Tip {
    title: string;
    rows: TipRow[];
    note?: ReactNode;
}

interface Shown extends Tip {
    left: number;
    top: number;
    flip: boolean;
}

/**
 * A tooltip for the bars of a chart, in the style of the dashboard instead of the browser's: the moment as title and
 * one row per figure, with the colour of its part of the bar. `bind(tip)` goes on each bar; the tip stands next to
 * the bar at the top of the chart, on its left near the right edge, so it never covers the bar or leaves the card.
 */
export const useBarTip = () => {
    const host = useRef<HTMLDivElement>(null);
    const [shown, setShown] = useState<Shown>();

    const bind = useCallback(
        (tip: Tip) => ({
            onMouseEnter: (e: React.MouseEvent<HTMLElement>) => {
                const box = host.current?.getBoundingClientRect();
                const bar = e.currentTarget.getBoundingClientRect();
                if (!box) return;
                const center = bar.left + bar.width / 2 - box.left;
                const flip = center > box.width * 0.6;
                // Beside the bar, at the top of the chart: never over the bar itself and never out of the card.
                setShown({ ...tip, left: flip ? bar.left - box.left - 8 : bar.right - box.left + 8, top: 0, flip });
            },
            onMouseLeave: () => setShown(undefined),
        }),
        []
    );

    const layer = shown ? (
        <div className={`bartip ${shown.flip ? 'flip' : ''}`} style={{ left: shown.left, top: Math.max(0, shown.top) }} role="tooltip">
            <div className="bt-title">{shown.title}</div>
            {shown.rows.map((r) => (
                <div key={r.label} className="bt-row">
                    <span className="bt-sw" style={{ background: r.color ?? 'transparent' }} />
                    <span className="bt-l">{r.label}</span>
                    <span className="bt-v">{r.value}</span>
                </div>
            ))}
            {shown.note && <div className="bt-note">{shown.note}</div>}
        </div>
    ) : null;

    return { host, bind, layer };
};
